import { set } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { LocalMessageSubPub, useLocalMessagingContext, FilterHelper as Flt } from "../LocalMessagingRoot";
import { promiseWithResolvers } from "~/utils/promiseWithResolvers";


const ASSEMBLE_CMD_TOPIC = 'OD_LM_SP_ASSEMBLE_CMD_TOPIC'
const INFORM_DIRTY_CMD_TOPIC = 'OD_LM_SP_INFORM_DIRTY_CMD_TOPIC'

interface AsyncFunc<T extends any = any> {
  (x:T): Promise<any>
}

interface UseLmStatesPropagateProps {
  key: string,
  asUpperStage?: {
    dirtyStateInformed: {
      handler: (informDirty: (dirty:boolean)=>void) => (subStateKey:string, dirty:boolean) => void,
      deps: any[],
    },
  },
  asLowerStage?: {
    collectStates: {
      handler: (assembleSubStates: AsyncFunc<string[]>) => AsyncFunc<void>,
      deps: any[],
    }
  },
}

interface StatesPropagateActions {
  assembleSubStates: AsyncFunc<string[]>;
  informDirty: (dirty:boolean)=>void;
}

interface AssembleCmdPayload {
  reportStates: (key: string, substate:any) => void;
  reportError: (key: string, errMsg: string) => void;
  targetKeys: Set<string>;
}
interface InformDirtyMsgPayload {
  subStateKey: string;
  dirty: boolean;
}

export const useLmStatesPropagate = (props: UseLmStatesPropagateProps, subPub?: LocalMessageSubPub): StatesPropagateActions => {
  const ctx = useLocalMessagingContext()
  const subscribe = subPub?.subscribe ?? ctx.subscribe
  const publish = subPub?.publish ?? ctx.publish
  if (subscribe == null || publish == null) { throw 'Should use this hook inside a LocalMessagingContext' }
  
  const key = props.key
  const collectStatesHandlerFac = props.asLowerStage?.collectStates.handler ?? EmptyCollectStateHandlerFac
  const collectStatesDeps = props.asLowerStage?.collectStates.deps ?? []

  const informDirtyHandlerFac = props.asUpperStage?.dirtyStateInformed.handler ?? EmptyInformDirtyHandlerFac
  const informDirtyDeps = props.asUpperStage?.dirtyStateInformed.deps ?? []

  const {assembleSubStates, informDirty} = useMemo(() => {
    let prevDirty = null as boolean|null
    return ({
      assembleSubStates: (subStateKeys: string[]) => {
        if (!subStateKeys || subStateKeys.length === 0) { return Promise.reject() }
        const qAssembleProcess = promiseWithResolvers()
        const keysCounter = new Set(subStateKeys)
        const result = {} as Record<string, any>
        let hasError = false
        const payload: AssembleCmdPayload = Object.freeze({
          reportStates: (subkey: string, substate:any) => {
            if (keysCounter.has(subkey)) {
              keysCounter.delete(subkey)
              set(result, subkey, substate)
            }
            if (keysCounter.size === 0) {
              hasError ? qAssembleProcess.reject(result) : qAssembleProcess.resolve(result)
            }
          },
          reportError: (subkey: string, errMsg: string) => {
            if (keysCounter.has(subkey)) {
              keysCounter.delete(subkey)
              set(result, subkey, new Error(errMsg))
              hasError = true
            }
            if (keysCounter.size === 0) {
              qAssembleProcess.reject(result)
            }
          },
          targetKeys: Object.freeze(new Set(subStateKeys))
        })
        
        const qTimeout = promiseWithResolvers()
        const timer = setTimeout(() => {
          const msg = 
            'Timeout waiting for substates to settle. This may indicating mis-arrangement of subStateKeys.\n  ' + 
            JSON.stringify({key, unsettled: [...payload.targetKeys]})
          console.error(msg)
          qTimeout.reject(msg)
        }, 800)
        qAssembleProcess.promise.then(() => {
          window.clearTimeout(timer)
        })
        
        publish({
          topic: ASSEMBLE_CMD_TOPIC,
          payload,
        }, Flt.withRelativeAddress.Children)

        return Promise.race([qAssembleProcess.promise, qTimeout.promise])
      },
      informDirty: (dirty:boolean) => {
        dirty = !!dirty
        if (prevDirty !== dirty) {
          prevDirty = dirty
          const payload: InformDirtyMsgPayload = {dirty, subStateKey: key}
          publish({
            topic: INFORM_DIRTY_CMD_TOPIC,
            payload,
          }, Flt.withRelativeAddress.Parent)
        }
      },
    })
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publish])


  const collectStates = useMemo(() => {
    return {
      handler: collectStatesHandlerFac(assembleSubStates as any),
      skipCollect: collectStatesHandlerFac === EmptyCollectStateHandlerFac,
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, collectStatesDeps)

  useEffect(() => {
    return subscribe(
      collectStates.skipCollect 
      ? [Flt.withRelativeAddress.None]
      : [Flt.withTopic(ASSEMBLE_CMD_TOPIC), Flt.withRelativeAddress.Parent, Flt.withCustom('target_me', (m) => (m.payload as AssembleCmdPayload).targetKeys.has(key))]
      , 
      (m) => {
        const cmd = m.payload as AssembleCmdPayload
        collectStates.handler().then(
          (data) => { cmd.reportStates(key, data) },
          (errMsg) => { cmd.reportError(key, errMsg) }
        )
      })
  }, [subscribe, collectStates, key])

  const dirtyStateInformed = useMemo(() => {
    return {
      handler: informDirtyHandlerFac(informDirty),
      skipAction: informDirtyHandlerFac === EmptyInformDirtyHandlerFac,
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, informDirtyDeps)

  useEffect(() => {
    return subscribe(
      dirtyStateInformed.skipAction
      ? [Flt.withRelativeAddress.None]
      : [Flt.withTopic(INFORM_DIRTY_CMD_TOPIC), Flt.withRelativeAddress.Children]
      ,
      (m) => {
        const p = (m.payload as InformDirtyMsgPayload)
        dirtyStateInformed.handler(p.subStateKey, p.dirty)
      }
    )
  }, [subscribe, dirtyStateInformed, key])

  return {assembleSubStates, informDirty}
}

const EmptyCollectStateHandlerFac = () => () => Promise.resolve(null)

const EmptyInformDirtyHandlerFac = () => () => {}
